import { useCallback, useEffect, useState } from 'react';

export const ACTIVE_LOAD_STORAGE_KEY = 'deliveriesActiveLoad';

export const calendarDateInTimeZone = (value, timeZone) => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date?.getTime?.())) return null;

  try {
    const DateTimeFormatter = Intl?.DateTimeFormat;
    const formatter = new DateTimeFormatter('en-US', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter?.formatToParts?.(date);
    const year = parts?.find?.(part => part?.type === 'year')?.value;
    const month = parts?.find?.(part => part?.type === 'month')?.value;
    const day = parts?.find?.(part => part?.type === 'day')?.value;

    return year && month && day ? `${year}-${month}-${day}` : null;
  } catch {
    return null;
  }
};

export const isScheduledForToday = (scheduledYardTime, timeZone) => {
  const scheduledDate = calendarDateInTimeZone(scheduledYardTime, timeZone);
  const today = calendarDateInTimeZone(new Date(), timeZone);
  return Boolean(scheduledDate && today && scheduledDate === today);
};

const DATE_OPTIONS_INCLUDING_TODAY = ['Today', 'Last_7_Days'];

export const doesDateFilterIncludeToday = (queryParams, timeZone) => {
  const today = calendarDateInTimeZone(new Date(), timeZone);
  if (!today) return false;
  if (DATE_OPTIONS_INCLUDING_TODAY.includes(queryParams?.dateOption)) return true;
  if (queryParams?.startDate && queryParams?.endDate) {
    const start = calendarDateInTimeZone(queryParams.startDate, timeZone);
    const end = calendarDateInTimeZone(queryParams.endDate, timeZone);
    return Boolean(start && end && today >= start && today <= end);
  }
  return false;
};

const readActiveLoadRecord = () => {
  try {
    const raw = localStorage?.getItem?.(ACTIVE_LOAD_STORAGE_KEY);
    if (!raw) return null;

    const record = JSON?.parse?.(raw);
    return record?.scheduledLoadRef ? record : null;
  } catch {
    return null;
  }
};

const clearActiveLoadRecord = () => {
  localStorage?.removeItem?.(ACTIVE_LOAD_STORAGE_KEY);
};

export const resolveActiveLoadForVehicle = ({ entityRef, vehicleId, timeZone } = {}) => {
  const record = readActiveLoadRecord();
  if (!record) return null;

  const sameEntity = record?.entityRef === entityRef;
  const sameVehicle = String(record?.vehicleId ?? '') === String(vehicleId ?? '');
  if (!sameEntity || !sameVehicle) return null;

  const today = calendarDateInTimeZone(new Date(), timeZone);
  if (!today || !record?.activatedOn || record?.activatedOn < today) {
    clearActiveLoadRecord();
    return null;
  }

  return record;
};

export const useActiveLoadAssignment = ({
  entityRef,
  vehicleId,
  timeZone,
  visibleLoadRefs = [],
  refreshToken,
  // Same-day expiry deletes storage, so stay idle until the company timezone is known.
  enabled = true,
} = {}) => {
  const [record, setRecord] = useState(null);

  const syncFromStorage = useCallback(() => {
    if (!enabled) {
      setRecord(null);
      return;
    }

    const nextRecord = resolveActiveLoadForVehicle({ entityRef, vehicleId, timeZone });
    setRecord(nextRecord);
  }, [enabled, entityRef, vehicleId, timeZone]);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage, refreshToken]);

  useEffect(() => {
    const expiryInterval = window?.setInterval?.(syncFromStorage, 60000);
    window?.addEventListener?.('focus', syncFromStorage);

    return () => {
      window?.clearInterval?.(expiryInterval);
      window?.removeEventListener?.('focus', syncFromStorage);
    };
  }, [syncFromStorage]);

  const activate = useCallback(
    assignment => {
      const scheduledLoadRef = assignment?.scheduledLoadRef;
      if (!enabled || !scheduledLoadRef || !entityRef || !vehicleId) return;
      if (!isScheduledForToday(assignment?.scheduledYardTime, timeZone)) return;

      const currentRecord = resolveActiveLoadForVehicle({ entityRef, vehicleId, timeZone });
      if (currentRecord?.scheduledLoadRef && currentRecord?.scheduledLoadRef !== scheduledLoadRef) {
        return;
      }

      const nextRecord = {
        entityRef,
        vehicleId,
        scheduledLoadRef,
        activatedOn: calendarDateInTimeZone(new Date(), timeZone),
      };
      localStorage?.setItem?.(ACTIVE_LOAD_STORAGE_KEY, JSON?.stringify?.(nextRecord));
      setRecord(nextRecord);
    },
    [enabled, entityRef, vehicleId, timeZone]
  );

  const deactivate = useCallback(
    scheduledLoadRef => {
      if (!enabled) {
        setRecord(null);
        return;
      }

      const currentRecord = resolveActiveLoadForVehicle({ entityRef, vehicleId, timeZone });
      if (!currentRecord) {
        setRecord(null);
        return;
      }
      if (scheduledLoadRef && currentRecord?.scheduledLoadRef !== scheduledLoadRef) return;

      clearActiveLoadRecord();
      setRecord(null);
    },
    [enabled, entityRef, vehicleId, timeZone]
  );

  const activeScheduledLoadRef = record?.scheduledLoadRef ?? null;
  const isStaleActive = Boolean(activeScheduledLoadRef) && !visibleLoadRefs?.includes?.(activeScheduledLoadRef);

  return {
    activeScheduledLoadRef,
    hasActiveLoad: Boolean(activeScheduledLoadRef),
    isStaleActive,
    activate,
    deactivate,
  };
};
